import React, { useState, useEffect } from "react";
import Orders from "../../pages/orders/OrdersTableItem.jsx";
import PastOrderModal from "./PastOrderModal.jsx";
import CustomerDetailModal from "../../components/CustomerDetailModal.jsx";
import { useSmallSize } from "../../utils/UseSmallSize.jsx";
import moment from "moment";
import CanView from "@/components/CanView.jsx";
import { useLocation, useNavigate, useParams } from "react-router-dom";

function PastOrdersTable({ orders, forceReload }) {
  const formatGroupedDate = (date) => {
    const dateMoment = moment(date);
    if (dateMoment.year() === moment().year()) {
      return dateMoment.format("MMM DD");
    }
    return dateMoment.format("MMM DD, YYYY");
  };

  const ordersGroupedByDate = orders.reduce((acc, order) => {
    const date = moment(order.order_completed || order.order_date).format("YYYY-MM-DD");
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(order);
    return acc;
  }, {});
  const [viewOrderID, setViewOrderID] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [selectedCustomerEmail, setSelectedCustomerEmail] = useState(null);
  const isSmallSize = useSmallSize();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const handleOpenCustomerModal = (customerEmail) => {
    setSelectedCustomerEmail(customerEmail);
    setCustomerModalOpen(true);
  };
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryId = searchParams.get("id");
    const routeId = params.id;
    const targetId = routeId || queryId;

    // If opened via ?id=xxx, normalize to /past-orders/xxx
    if (!routeId && queryId) {
      navigate(`/past-orders/${queryId}`, { replace: true });
      return;
    }

    if (targetId) {
      setTimeout(() => setViewOrderID(targetId), 150);
    }
  }, [location.search, params.id, navigate]);

  const handleOpenOrder = (orderID) => {
    navigate(`/past-orders/${orderID}`);
  };

  if ((orders || []).length === 0) {
    return (
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative overflow-x-auto">
        <div className="text-center p-4 text-slate-500 dark:text-slate-400">
          No orders for the selected date range
        </div>
        <PastOrderModal
          setModalOpen={(open) => {
            if (!open) {
              setViewOrderID(null);
              if (params.id) {
                navigate("/past-orders", { replace: true });
              } else if (location.search.includes("id=")) {
                navigate(location.pathname, { replace: true });
              }
            } else {
              // opening from inside modal isn't used; no-op
            }
          }}
          modalOpen={Boolean(viewOrderID)}
          orderID={viewOrderID}
          forceReload={forceReload}
        />
      </div>
    );
  }
  return (
    <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
      <div>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
            {/* Table header */}
            {!isSmallSize && (
              <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Order</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Date</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Customer</div>
                  </th>
                  <CanView directOnly>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Type</div>
                  </th>
                  </CanView>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Items</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Status</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Total</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">View</div>
                  </th>
                </tr>
              </thead>
            )}
            {/* Table body */}
            {!isSmallSize &&
              orders.map((order) => {
                return (
                  <Orders
                    isPast
                    key={order._id}
                    order={order}
                    handleOpenOrder={handleOpenOrder}
                    onOpenCustomerModal={handleOpenCustomerModal}
                  />
                );
              })}
            {isSmallSize &&
              Object.keys(ordersGroupedByDate).map((date) => {
                return (
                  <React.Fragment key={date}>
                    <tr>
                      <td
                        colSpan="8"
                        className="px-2 py-1.5 whitespace-nowrap text-sm bg-slate-50"
                      >
                        <div className="flex justify-between">
                          <div className="font-semibold text-left text-slate-500 dark:text-slate-400">
                            {formatGroupedDate(date)}{" "}
                          </div>
                          <div className="font-semibold text-left text-slate-500 dark:text-slate-400">
                            <span className="text-slate-400 dark:text-slate-500 font-medium">
                              {ordersGroupedByDate[date].length}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {ordersGroupedByDate[date].map((order) => {
                      return (
                        <Orders
                          isPast
                          key={order._id}
                          order={order}
                          handleOpenOrder={handleOpenOrder}
                          isSmallSize={isSmallSize}
                          onOpenCustomerModal={handleOpenCustomerModal}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
          </table>
        </div>
      </div>
      <PastOrderModal
        setModalOpen={(open) => {
          if (!open) {
            setViewOrderID(null);
            if (params.id) {
              navigate("/past-orders", { replace: true });
            } else if (location.search.includes("id=")) {
              navigate(location.pathname, { replace: true });
            }
          } else {
            // opening from inside modal isn't used; no-op
          }
        }}
        modalOpen={Boolean(viewOrderID)}
        orderID={viewOrderID}
        forceReload={forceReload}
      />

      {/* Customer Detail Modal */}
      <CustomerDetailModal
        modalOpen={customerModalOpen}
        setModalOpen={setCustomerModalOpen}
        customerEmail={selectedCustomerEmail}
        title="Customer Profile"
      />
    </div>
  );
}

export default PastOrdersTable;
